/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { apiClient } from '@lib/api-client';
import { queryClient } from '@lib/react-query';
import { notifications } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';

// I18n
import { useTranslation } from 'react-i18next';

// Types
import { IApiError } from '@global-types/index';
import { TBodyApi } from 'shared/types';

/* -------------------------------------------------------------------------- */
/*                                     Type                                   */
/* -------------------------------------------------------------------------- */

type TApiResponse = {
	sellPrice_europertco2eq: number;
	costPrice_europertco2eq: number;
	entity: string;
	name: string;
	location: {
		countryCode: string;
		place: string;
	};
	unit: string;
	technology: string;
	active: boolean;
	logo: string | null;
	cover: string;
	publicSpecs: string[];
	quantity: {
		total_aco2: number;
		held_aco2: number;
		burnt_aco2: number;
		remaining_aco2: number;
		unprocessed_gco2eq: number;
	};
	organization: string;
	createdAt: string;
	updatedAt: string;
	description: string;
	id: string;
};

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const updateAdditionalityProject = ({
	data,
	id,
}: {
	data: TBodyApi;
	id: string;
}): Promise<TApiResponse> => {
	return apiClient.patch(`/additionalities/${id}`, data);
};

export const useUpdateAdditionalityProject = (
	page: number,
	size: number
) => {
	const { t } = useTranslation();
	const {
		mutate,
		isPending: isLoading,
		isSuccess,
	} = useMutation({
		mutationFn: updateAdditionalityProject,

		onSuccess: async (response) => {
			notifications.show({
				title: t('global.success'),
				message: t(
					'measurabilityProjects.notifications.measurabilityProjectSuccessfullyUpdated'
				),
				color: 'var(--mantine-color-igluGreen-9)',
			});
			const id = response.id;

			queryClient.invalidateQueries({
				queryKey: ['additionality-project-update', id],
			});

			queryClient.invalidateQueries({
				queryKey: ['additionality-projects', id],
			});
			queryClient.invalidateQueries({
				queryKey: [
					'additionality-projects',
					{ page: page, size: size },
				],
			});
		},
		onError: (error: IApiError) => {
			notifications.show({
				title: t('global.error'),
				message: t(`api.${error.type}`),
				color: 'var(--mantine-color-igluRed-9)',
			});
		},
	});

	return { mutate, isLoading, isSuccess };
};
